import secureApi from "../../utils/secureApi";

export type VideoType = "live_stream" | "highlight";
export type VideoCategory = "match_coverage" | "ceremony" | "award" | "other";
export type ManualStatus = "upcoming" | "live" | "past";
export type AccessType = "free" | "paid" | "exclusive_paid";
export type VideoAccessType = AccessType | "inherit";
export interface VideoPricing {
  IN: {
    amount: number;
    currency: "INR";
  };
}

export interface LinkedMatch {
  matchId: string;
  sport: string;
}

export interface LiveStreamInfo {
  channelName?: string | null;
  channelArn?: string | null;
  ingestEndpoint?: string | null;
  streamKey?: string | null;
  streamId?: string | null;
  playbackUrl?: string | null;
  streamStartedAt?: string | null;
  streamEndedAt?: string | null;
  status?: string | null;
}

export interface StreamRecordingInfo {
  streamS3Url?: string | null;
  streamThumbnail?: string | null;
  recordingStartedAt?: string | null;
  recordingEndedAt?: string | null;
  recordingUrls?: string[];
}

export interface SflTvVideo {
  _id: string;
  tournament: string;
  tournamentSeason: string;
  date: string;
  dateTime?: string;
  type: VideoType;
  category?: VideoCategory;
  ageCategory?: string;
  venue?: string;
  title: string;
  subtitle?: string;
  section?: string | null;
  thumbnailUrl?: string;
  sortOrder: number;
  isActive: boolean;
  youtubeUrl?: string;
  manualStatus?: ManualStatus;
  accessType: VideoAccessType;
  pricing?: VideoPricing;
  monetizationProduct?: unknown;
  videoUrl?: string;
  duration?: number;
  linkedMatches?: LinkedMatch[];
  liveStreamInfo?: LiveStreamInfo;
  streamRecordingInfo?: StreamRecordingInfo;
  createdAt?: string;
  updatedAt?: string;
}

export type VideoPayload = Omit<SflTvVideo, "_id" | "liveStreamInfo" | "streamRecordingInfo" | "createdAt" | "updatedAt" | "monetizationProduct">;
export type VideoListFilters = Partial<Pick<SflTvVideo, "tournament" | "tournamentSeason" | "date" | "isActive">>;

const ROOT = "/api/v1/sfl-tv/admin/videos";
const config = { headers: { "x-user-role": "academy-admin" } };

const unwrap = <T,>(response: any): T => (response?.data ?? response) as T;
const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? error?.message ?? fallback;

export const buildVideoListParams = (filters: VideoListFilters) =>
  Object.entries(filters).reduce<Record<string, string | boolean>>((params, [key, value]) => {
    if (value !== undefined && value !== "") params[key] = value as string | boolean;
    return params;
  }, {});

export const canGenerateIvsCredentials = (video: Pick<SflTvVideo, "type" | "youtubeUrl" | "liveStreamInfo">) =>
  video.type === "live_stream" && !video.liveStreamInfo?.channelArn;

export const sanitizeVideoPayload = (payload: Partial<VideoPayload>): Partial<VideoPayload> => {
  const {
    _id,
    liveStreamInfo,
    streamRecordingInfo,
    createdAt,
    updatedAt,
    linkedMatch,
    monetizationProduct,
    ...writablePayload
  } = payload as Partial<SflTvVideo> & { linkedMatch?: unknown };

  if (writablePayload.type !== "live_stream") return writablePayload;
  const { videoUrl, duration, ...liveStreamPayload } = writablePayload;
  return liveStreamPayload;
};

export async function listVideos(filters: VideoListFilters = {}): Promise<SflTvVideo[]> {
  try {
    return unwrap<SflTvVideo[]>(await secureApi.get(ROOT, { ...config, params: buildVideoListParams(filters) })) ?? [];
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load videos."));
  }
}

export async function getVideo(id: string): Promise<SflTvVideo> {
  try {
    return unwrap<SflTvVideo>(await secureApi.get(`${ROOT}/${id}`, config));
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load video."));
  }
}

export async function createVideo(payload: VideoPayload): Promise<SflTvVideo> {
  try {
    return unwrap<SflTvVideo>(await secureApi.post(ROOT, sanitizeVideoPayload(payload), config));
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to create video."));
  }
}

export async function updateVideo(id: string, payload: Partial<VideoPayload>): Promise<SflTvVideo> {
  try {
    return unwrap<SflTvVideo>(await secureApi.patch(`${ROOT}/${id}`, sanitizeVideoPayload(payload), config));
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to update video."));
  }
}

export async function deleteVideo(id: string): Promise<void> {
  try {
    await secureApi.delete(`${ROOT}/${id}`, config);
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to delete video."));
  }
}

export async function generateIvsCredentials(id: string): Promise<SflTvVideo> {
  try {
    return unwrap<SflTvVideo>(await secureApi.post(`${ROOT}/${id}/create-channel`, undefined, config));
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to generate IVS credentials."));
  }
}
